<template>
    <div class="smiley-rating"
         :class="{ 'is-disabled': disabled, 'has-value': selected !== null, 'is-compact': compact }"
         role="radiogroup"
         :aria-label="ariaLabel">
        <button v-for="(face, i) in faces"
                :key="face.id"
                type="button"
                class="smiley-rating__option"
                :class="{ 'is-selected': selected === face.id, 'is-hovered': hovered === face.id }"
                :style="optionStyle(face)"
                role="radio"
                :aria-checked="selected === face.id ? 'true' : 'false'"
                :aria-label="$t(face.name)"
                :tabindex="tabIndexFor(face, i)"
                :disabled="disabled"
                @click="select(face.id)"
                @mouseenter="hovered = face.id"
                @mouseleave="hovered = null"
                @focus="hovered = face.id"
                @blur="hovered = null"
                @keydown.left.prevent="move(1)"
                @keydown.right.prevent="move(-1)"
                @keydown.up.prevent="move(-1)"
                @keydown.down.prevent="move(1)"
                @keydown.space.prevent="select(face.id)"
                @keydown.enter.prevent="select(face.id)">
            <span class="smiley-rating__disc" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="smiley-rating__svg">
                    <path :d="face.filled" class="smiley-rating__glyph" />
                </svg>
            </span>
            <span class="smiley-rating__label">{{ $t(face.name) }}</span>
        </button>
    </div>
</template>

<script>
    /*
     * Five-level emoji (smiley) rating.
     * v-model contract: emits the face id (1 = Terrible ... 5 = Great), the same values the
     * previous implementation produced, so stored answers stay compatible.
     */
    export default {
        name: 'SmileyRating',
        props: {
            value: { type: [Number, String], default: null },
            disabled: { type: Boolean, default: false },
            compact: { type: Boolean, default: false },
            ariaLabel: { type: String, default: '' }
        },
        data() {
            return {
                hovered: null,
                selected: this.normalize(this.value),
                faces: [
                    {
                        id: 1,
                        name: 'Terrible',
                        color: '#DC2626',
                        text: '#991B1B',
                        tint: '#FEE2E2',
                        filled: 'M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2M7 9.5V8l3 1.5c0 .8-.7 1.5-1.5 1.5S7 10.3 7 9.5m7.77 7.73C14.32 16.5 13.25 16 12 16s-2.32.5-2.77 1.23l-1.42-1.42C8.71 14.72 10.25 14 12 14c1.75 0 3.29.72 4.19 1.81zM17 9.5c0 .8-.7 1.5-1.5 1.5S14 10.3 14 9.5L17 8z'
                    },
                    {
                        id: 2,
                        name: 'Bad',
                        color: '#EA7A1F',
                        text: '#9A4A0E',
                        tint: '#FDEBD9',
                        filled: 'M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2M7 9.5C7 8.7 7.7 8 8.5 8s1.5.7 1.5 1.5S9.3 11 8.5 11S7 10.3 7 9.5m7.77 7.73C14.32 16.5 13.25 16 12 16s-2.32.5-2.77 1.23l-1.42-1.42C8.71 14.72 10.25 14 12 14c1.75 0 3.29.72 4.19 1.81zM15.5 11c-.8 0-1.5-.7-1.5-1.5S14.7 8 15.5 8s1.5.7 1.5 1.5s-.7 1.5-1.5 1.5'
                    },
                    {
                        id: 3,
                        name: 'Okay',
                        color: '#EAB308',
                        text: '#854D0E',
                        tint: '#FEF3C7',
                        filled: 'M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2M8.5 8A1.5 1.5 0 1 1 7 9.5A1.54 1.54 0 0 1 8.5 8m8.5 8h-4a4 4 0 0 0-3.47 2L7.8 17a6 6 0 0 1 5.2-3h4m-1.5-3A1.5 1.5 0 1 1 17 9.5a1.54 1.54 0 0 1-1.5 1.5'
                    },
                    {
                        id: 4,
                        name: 'Good',
                        color: '#84CC16',
                        text: '#3F6212',
                        tint: '#ECFCCB',
                        filled: 'M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2M7 9.5C7 8.7 7.7 8 8.5 8s1.5.7 1.5 1.5S9.3 11 8.5 11S7 10.3 7 9.5m5 7.73c-1.75 0-3.29-.73-4.19-1.81L9.23 14c.45.72 1.52 1.23 2.77 1.23s2.32-.51 2.77-1.23l1.42 1.42c-.9 1.08-2.44 1.81-4.19 1.81M15.5 11c-.8 0-1.5-.7-1.5-1.5S14.7 8 15.5 8s1.5.7 1.5 1.5s-.7 1.5-1.5 1.5'
                    },
                    {
                        id: 5,
                        name: 'Great',
                        color: '#0D9488',
                        text: '#115E59',
                        tint: '#CCFBF1',
                        filled: 'M12 2C6.47 2 2 6.5 2 12a10 10 0 0 0 10 10a10 10 0 0 0 10-10A10 10 0 0 0 12 2m3.5 6A1.5 1.5 0 0 1 17 9.5a1.5 1.5 0 0 1-1.5 1.5A1.5 1.5 0 0 1 14 9.5A1.5 1.5 0 0 1 15.5 8m-7 0A1.5 1.5 0 0 1 10 9.5A1.5 1.5 0 0 1 8.5 11A1.5 1.5 0 0 1 7 9.5A1.5 1.5 0 0 1 8.5 8m3.5 9.5c-2.33 0-4.31-1.46-5.11-3.5h10.22c-.81 2.04-2.78 3.5-5.11 3.5'
                    }
                ]
            }
        },
        watch: {
            value(val) {
                this.selected = this.normalize(val)
            }
        },
        methods: {
            normalize(val) {
                const n = Number(val)
                return n >= 1 && n <= 5 ? n : null
            },
            optionStyle(face) {
                return {
                    '--face-color': face.color,
                    '--face-text': face.text,
                    '--face-tint': face.tint,
                    '--i': face.id - 1
                }
            },
            tabIndexFor(face, i) {
                if (this.selected !== null) return this.selected === face.id ? 0 : -1
                return i === 0 ? 0 : -1
            },
            select(id) {
                if (this.disabled) return
                this.selected = id
                this.$emit('input', id)
                this.$emit('change', id)
            },
            move(step) {
                if (this.disabled) return
                const current = this.selected || this.hovered || 1
                const next = Math.min(5, Math.max(1, current + step))
                this.select(next)
                this.$nextTick(() => {
                    const btn = this.$el.querySelectorAll('.smiley-rating__option')[next - 1]
                    if (btn) btn.focus()
                })
            }
        }
    }
</script>

<style scoped>
    /* Tokens (Soft UI, light surface) */
    .smiley-rating {
        --sr-surface: #f3f6f5;
        --sr-card: #ffffff;
        --sr-text: #1f2a28;
        --sr-muted: #6b7a77;
        --sr-ring: #0a716e;
        --sr-radius: 18px;
        --sr-option-radius: 14px;
        --sr-disc: 64px;
        --sr-glyph: 44px;
        --sr-ease: cubic-bezier(.2, .8, .2, 1);

        display: grid;
        grid-template-columns: repeat(5, minmax(0, 1fr));
        gap: 6px;
        width: 100%;
        max-width: 600px;
        margin: 8px auto 0;
        padding: 8px;
        border-radius: var(--sr-radius);
        background: var(--sr-surface);
        direction: inherit;
    }

    .smiley-rating__option {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
        padding: 14px 4px 12px;
        border: 0;
        border-radius: var(--sr-option-radius);
        background: transparent;
        cursor: pointer;
        font: inherit;
        color: var(--sr-muted);
        transition: background-color .22s var(--sr-ease), box-shadow .22s var(--sr-ease), transform .22s var(--sr-ease);
        -webkit-tap-highlight-color: transparent;
    }

    .smiley-rating__option:focus { outline: none; }
    .smiley-rating__option:focus-visible {
        box-shadow: 0 0 0 2px var(--sr-card), 0 0 0 4px var(--sr-ring);
    }

    .smiley-rating__disc {
        display: flex;
        align-items: center;
        justify-content: center;
        width: var(--sr-disc);
        height: var(--sr-disc);
        border-radius: 50%;
        background: var(--sr-card);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .06), 0 4px 12px -4px rgba(15, 23, 42, .14);
        transition: background-color .22s var(--sr-ease), box-shadow .22s var(--sr-ease), transform .22s var(--sr-ease);
    }

    .smiley-rating__svg {
        width: var(--sr-glyph);
        height: var(--sr-glyph);
        display: block;
    }

    .smiley-rating__glyph {
        fill: var(--face-color);
        transition: fill .22s var(--sr-ease);
    }

    .smiley-rating__label {
        font-size: 13px;
        font-weight: 600;
        line-height: 1.25;
        text-align: center;
        color: var(--sr-muted);
        transition: color .22s var(--sr-ease);
    }

    /* Hover: the option becomes a card and the face lifts */
    .smiley-rating__option.is-hovered {
        background: var(--sr-card);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 20px -10px rgba(15, 23, 42, .18);
    }
    .smiley-rating__option.is-hovered .smiley-rating__disc {
        transform: translateY(-3px);
        background: var(--face-tint);
    }
    .smiley-rating__option.is-hovered .smiley-rating__label {
        color: var(--sr-text);
    }

    /* Selected: solid sentiment disc, white glyph, card with coloured glow */
    .smiley-rating__option.is-selected {
        background: var(--sr-card);
        box-shadow: 0 1px 2px rgba(15, 23, 42, .05), 0 12px 28px -12px var(--face-color);
    }
    .smiley-rating__option.is-selected .smiley-rating__disc {
        background: var(--face-color);
        transform: scale(1.06);
        box-shadow: 0 6px 16px -6px var(--face-color);
        animation: smiley-settle .32s var(--sr-ease);
    }
    .smiley-rating__option.is-selected .smiley-rating__glyph {
        fill: #fff;
    }
    .smiley-rating__option.is-selected .smiley-rating__label {
        color: var(--face-text);
        font-weight: 700;
    }

    /* Once a choice is made, the others step back */
    .smiley-rating.has-value .smiley-rating__option:not(.is-selected):not(.is-hovered) .smiley-rating__glyph {
        fill: #b8c4c1;
    }

    .smiley-rating.is-disabled .smiley-rating__option { cursor: default; }
    .smiley-rating.is-disabled .smiley-rating__option:not(.is-selected) { opacity: .6; }

    /* Compact variant (builder preview) */
    .smiley-rating.is-compact {
        --sr-disc: 52px;
        --sr-glyph: 36px;
        max-width: 520px;
        margin: 0;
    }
    .smiley-rating.is-compact .smiley-rating__option { padding: 10px 4px 8px; gap: 8px; }
    .smiley-rating.is-compact .smiley-rating__label { font-size: 12px; }

    /* Motion: staggered entrance, hover wiggle, select bounce + ripple */
    .smiley-rating__option {
        animation: smiley-in .5s var(--sr-ease) both;
        animation-delay: calc(var(--i, 0) * 70ms);
    }
    .smiley-rating__disc { position: relative; }
    .smiley-rating__option.is-hovered:not(.is-selected) .smiley-rating__svg {
        animation: smiley-wiggle .55s ease-in-out;
    }
    .smiley-rating__option.is-selected .smiley-rating__disc {
        animation: smiley-bounce .6s cubic-bezier(.34, 1.56, .64, 1);
    }
    .smiley-rating__option.is-selected .smiley-rating__svg {
        animation: smiley-cheer .6s var(--sr-ease);
    }
    .smiley-rating__option.is-selected .smiley-rating__disc::after {
        content: '';
        position: absolute;
        inset: -3px;
        border-radius: 50%;
        border: 2px solid var(--face-color);
        opacity: 0;
        animation: smiley-ripple .7s ease-out;
        pointer-events: none;
    }

    @keyframes smiley-in {
        0%   { opacity: 0; transform: translateY(10px) scale(.9); }
        100% { opacity: 1; transform: translateY(0) scale(1); }
    }
    @keyframes smiley-wiggle {
        0%   { transform: rotate(0deg) scale(1); }
        25%  { transform: rotate(-10deg) scale(1.08); }
        50%  { transform: rotate(9deg) scale(1.08); }
        75%  { transform: rotate(-5deg) scale(1.04); }
        100% { transform: rotate(0deg) scale(1); }
    }
    @keyframes smiley-bounce {
        0%   { transform: translateY(0) scale(.9); }
        35%  { transform: translateY(-12px) scale(1.14); }
        65%  { transform: translateY(2px) scale(1.02); }
        100% { transform: translateY(0) scale(1.06); }
    }
    @keyframes smiley-cheer {
        0%   { transform: rotate(0deg); }
        30%  { transform: rotate(-12deg); }
        60%  { transform: rotate(10deg); }
        100% { transform: rotate(0deg); }
    }
    @keyframes smiley-ripple {
        0%   { opacity: .9; transform: scale(1); }
        100% { opacity: 0; transform: scale(1.6); }
    }

    @media (max-width: 600px) {
        .smiley-rating { --sr-disc: 48px; --sr-glyph: 32px; gap: 2px; padding: 6px; max-width: 100%; }
        .smiley-rating__option { padding: 10px 2px 8px; gap: 8px; }
        .smiley-rating__label { font-size: 11px; }
    }

    @media (prefers-reduced-motion: reduce) {
        .smiley-rating__option,
        .smiley-rating__disc,
        .smiley-rating__glyph,
        .smiley-rating__label {
            transition: none;
        }
        .smiley-rating__option,
        .smiley-rating__option.is-selected .smiley-rating__disc,
        .smiley-rating__option.is-selected .smiley-rating__svg,
        .smiley-rating__option.is-selected .smiley-rating__disc::after,
        .smiley-rating__option.is-hovered:not(.is-selected) .smiley-rating__svg {
            animation: none;
        }
    }
</style>
